/**
 * One row of a page list: a 14px icon, a title that truncates, then
 * fixed-width trailing columns. Spec beside it in list-row.md.
 *
 * @module components/ui/list-row
 */
import { mergeProps } from "@base-ui/react/merge-props";
import { useRender } from "@base-ui/react/use-render";
import type { ComponentProps } from "react";

import { cn } from "~/lib/utils";

const LIST_ROW_CLASS =
  "group/list-row flex min-h-8 w-full min-w-0 items-center gap-2 rounded-md px-2 text-left text-[14px] outline-none transition-colors duration-[120ms] hover:bg-accent/70 focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring/70";

export function ListRow({ className, render, ...props }: useRender.ComponentProps<"a">) {
  // The slot is set last, so a wrapping trigger (a `Tip`) cannot rename it.
  return useRender({
    defaultTagName: "a",
    props: { ...mergeProps<"a">({ className: cn(LIST_ROW_CLASS, className) }, props), "data-slot": "list-row" },
    render,
  });
}

export function ListRowIcon({ className, ...props }: ComponentProps<"span">) {
  return (
    <span
      data-slot="list-row-icon"
      className={cn(
        "flex size-3.5 shrink-0 items-center justify-center text-muted-foreground [&>svg]:size-3.5 [&>svg]:stroke-[1.75]",
        className,
      )}
      {...props}
    />
  );
}

const TITLE_INK = {
  /** Full ink. */
  default: "text-foreground",
  /** Slightly receded, full ink while the row is hovered. */
  soft: "text-foreground/90 group-hover/list-row:text-foreground",
  /** Done or read: stays receded. */
  quiet: "text-foreground/80",
} as const;

export function ListRowTitle({
  ink = "default",
  className,
  ...props
}: ComponentProps<"span"> & { readonly ink?: keyof typeof TITLE_INK }) {
  return <span data-slot="list-row-title" className={cn("min-w-0 flex-1 truncate", TITLE_INK[ink], className)} {...props} />;
}

/** A fixed-width column, so one field lines up from row to row; shown once the list is 34rem wide. */
export function ListRowMeta({ className, ...props }: ComponentProps<"span">) {
  return (
    <span
      data-slot="list-row-meta"
      className={cn("hidden w-36 shrink-0 truncate text-xs text-muted-foreground @min-[34rem]/list:block", className)}
      {...props}
    />
  );
}

/** The right-hand date column; `urgent` turns it red. */
export function ListRowDate({
  urgent = false,
  className,
  ...props
}: ComponentProps<"span"> & { readonly urgent?: boolean }) {
  return (
    <span
      data-slot="list-row-date"
      className={cn(
        "min-w-14 shrink-0 whitespace-nowrap text-right text-xs tabular-nums",
        urgent ? "text-destructive" : "text-muted-foreground",
        className,
      )}
      {...props}
    />
  );
}
